import React, { useState, useRef, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import cn from 'classnames';
import Modal from 'react-modal';
import { useForm } from 'react-hook-form';

// components
import FolderContextMenu from '../StudioEditor/Projects/FolderContextMenu';

// helpers
import { STUDIO_FOLDER, STUDIO_PROJECTS_CARD } from '../../helpers/constants';

// hooks
import useClickOutside from '../../hooks/useClickOutside';

// types
import { DragItem, IStudioProjectsFolder } from '../../types/studio.types';

// styles
import '../../styles/studio/studio-content.scss';

interface IFolderRenameFormValue {
  name: string;
}

const ProjectsFolder = ({
  projectFolder,
  onProjectMoveToFolder,
  onFolderOpen,
  isProjectDragging,
  onFolderRename,
  onProjectFolderDuplicate,
  onProjectFolderDelete,
  index,
  moveFolder,
  id,
  checkIsFolderDragging,
  isFolderDuplicateLoading,
  rightClickedFolder,
  onSelectRightClickedFolder,
  isToolStudio,
  isDarkMode,
}: IStudioProjectsFolder) => {
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const [contextMenuXYPosition, setContextMenuXYPosition] = useState({
    x: 0,
    y: 0,
  });
  const [isFolderRenameModalVisible, setIsFolderRenameModalVisible] =
    useState(false);
  const [isFolderDeleteModalVisible, setIsFolderDeleteModalVisible] =
    useState(false);

  const folderRef = useRef<HTMLDivElement>(null);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<IFolderRenameFormValue>();

  const [{ isOver }, folderDrop] = useDrop({
    accept: [STUDIO_PROJECTS_CARD, STUDIO_FOLDER],
    hover(item: DragItem, monitor) {
      if (!folderRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index as number;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Only handle folder reordering if we're dragging a folder
      if (item.isFolder) {
        // Determine rectangle on screen
        const hoverBoundingRect = folderRef.current?.getBoundingClientRect();

        // Get vertical middle
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        // Determine mouse position
        const clientOffset = monitor.getClientOffset();

        // Get pixels to the top
        const hoverClientY = (clientOffset as any).y - hoverBoundingRect.top;

        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }

        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }

        // Call moveFolder for visual reordering only (no API update)
        moveFolder(dragIndex, hoverIndex as number, false);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        // eslint-disable-next-line no-param-reassign
        item.index = hoverIndex as number;
      }
      // If dragging a project, don't do anything in hover - let the drop handle it
    },
    drop(_item: DragItem, monitor) {
      const didDrop = monitor.didDrop();

      if (didDrop) {
        return;
      }

      // Handle folder reordering
      if (_item.isFolder) {
        const dragIndex = _item.index;
        const hoverIndex = index;

        // Call moveFolder with API update on drop
        moveFolder(dragIndex, hoverIndex as number, true);
        // eslint-disable-next-line no-param-reassign
        _item.index = hoverIndex as number;
      }
      // Handle project moving to folder
      else {
        onProjectMoveToFolder(+_item?.id, projectFolder?.id);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const [{ isDragging }, drag] = useDrag({
    type: STUDIO_FOLDER,
    item: () => {
      return { id, index, isFolder: true };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    checkIsFolderDragging(isDragging);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  const handleCloseContextMenu = () => {
    setIsContextMenuVisible(false);
  };

  const handleRightMouseClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsContextMenuVisible(false);

    const contextMenuOverflowsYAxis = window.innerHeight - event.pageY <= 185; // 185 - context menu height
    const contextMenuOverflowsXAxis = window.innerWidth - event.pageX <= 200; // 200 - proper amount of pixels to fit screen

    const positionChange = {
      x: contextMenuOverflowsXAxis ? window.innerWidth - 200 : event.pageX,
      y: contextMenuOverflowsYAxis ? window.innerHeight - 300 : event.pageY,
    };

    setContextMenuXYPosition(positionChange);
    setIsContextMenuVisible(true);
    onSelectRightClickedFolder(id);
  };

  const openFolderRenameModal = () => {
    setIsFolderRenameModalVisible(true);
  };

  const closeFolderRenameModal = () => {
    setIsFolderRenameModalVisible(false);
  };

  const onRenameFormSubmit = (formValues: IFolderRenameFormValue) => {
    onFolderRename({ id: projectFolder.id, ...formValues });
    setIsFolderRenameModalVisible(false);
  };

  const duplicateProjectFolder = () => {
    onProjectFolderDuplicate(projectFolder.id);
    setIsContextMenuVisible(false);
    onSelectRightClickedFolder(null);
  };

  const openFolderDeleteModal = () => {
    setIsFolderDeleteModalVisible(true);
  };

  const closeFolderDeleteModal = () => {
    setIsFolderDeleteModalVisible(false);
  };

  const onDeleteEmptyFolder = () => {
    onProjectFolderDelete(projectFolder.id);
    setIsFolderDeleteModalVisible(false);
  };

  useClickOutside(
    folderRef,
    () => {
      setIsContextMenuVisible(false);
    },
    'context-menu',
  );

  useClickOutside(
    folderRef,
    () => {
      setIsContextMenuVisible(false);
    },
    'context-menu',
    'touchstart',
    'touchend',
  );

  useEffect(() => {
    if (!isContextMenuVisible) {
      onSelectRightClickedFolder(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContextMenuVisible]);

  drag(folderDrop(folderRef));

  return (
    <>
      <div ref={folderRef} className="w-[200px] lg:w-[120px]">
        <div
          className={cn(
            'projects-p2d-container--brand-set-container--folder--container',
            {
              'drag-hover': isOver,
              hovered: rightClickedFolder === id,
              'light-hover': !isToolStudio && !isDarkMode,
            },
          )}
          ref={folderDrop}
          onClick={() => {
            onFolderOpen(projectFolder, projectFolder?.type);
            handleCloseContextMenu();
          }}
          onContextMenu={handleRightMouseClick}
        >
          <img
            src="/static/img/icons/brand-set-folder.svg"
            alt="folder"
            width={186}
            height={124}
            className="studio-projects-folders--folder--bigger"
          />
          {projectFolder.preview &&
            !projectFolder.preview.includes('placeholder') && (
              <img
                src={projectFolder.preview}
                alt="folder preview"
                width={160}
                height={115}
                className="projects-p2d-container--brand-set-container--folder--container--p2b-img studio-projects-folders--folder--image"
              />
            )}
          {/* {!(isProjectDragging || isOver) && (
            <img
              src="/static/img/icons/project-folder.svg"
              alt="folder"
              width={72}
              height={48}
              className="studio-projects-folders--folder--icon"
            />
          )} */}
          {/* {(isProjectDragging || isOver) && (
            <img
              src="/static/img/icons/project-folder-lighter.svg"
              alt="folder"
              width={72}
              height={48}
              className="studio-projects-folders--folder--icon"
            />
          )} */}
          <div className="studio-projects-folders--folder--details-container">
            <p
              className={cn(
                'studio-projects-folders--folder--details-container--title',
                {
                  darken: !isToolStudio && !isDarkMode,
                },
              )}
            >
              {projectFolder.formattedName}
            </p>
            <p
              className={cn(
                'studio-projects-folders--folder--details-container--designs-amount',
                {
                  'drag-hover': isOver,
                  'dark:!text-[#eee]': isToolStudio || isDarkMode,
                },
              )}
            >
              {projectFolder.projectTotal} designs
            </p>
            <div className="studio-projects-folders--folder--details-container--more-info-container">
              <img
                src={`/static/img/icons/more-info${
                  !isToolStudio && !isDarkMode ? '' : '-grey'
                }.svg`}
                alt="more info"
                className="studio-projects-folders--folder--details-container--more-info-container--three-dots"
              />
            </div>
          </div>
        </div>
      </div>
      <FolderContextMenu
        isVisible={isContextMenuVisible}
        position={contextMenuXYPosition}
        openFolderRenameModal={openFolderRenameModal}
        onProjectFolderDuplicate={duplicateProjectFolder}
        openFolderDeleteModal={
          projectFolder.projectTotal
            ? openFolderDeleteModal
            : onDeleteEmptyFolder
        }
        isFolderDuplicateLoading={isFolderDuplicateLoading}
        isToolStudio={isToolStudio}
      />
      <Modal
        isOpen={isFolderRenameModalVisible}
        ariaHideApp={false}
        onRequestClose={closeFolderRenameModal}
        className="Modal__Content dark"
        parentSelector={() => document.querySelector('#root') as HTMLElement}
      >
        <div className="studio-project-modal">
          <p className="studio-project-modal--label">
            Confirm renaming of folder?
          </p>
          <form className="form" onSubmit={handleSubmit(onRenameFormSubmit)}>
            <input
              type="text"
              className="studio-project-modal--input"
              placeholder="Type folder name here..."
              maxLength={30}
              {...register('name', {
                required: true,
                value: projectFolder.name,
              })}
            />
            {errors.name && (
              <span className="field-error">Field is required</span>
            )}
            <div className="studio-project-modal--btns-container">
              <button
                className="studio-project-modal--btns-container--btn cancel"
                type="button"
                onClick={closeFolderRenameModal}
              >
                cancel
              </button>
              <button
                className="studio-project-modal--btns-container--btn submit"
                type="submit"
              >
                rename
              </button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={isFolderDeleteModalVisible}
        ariaHideApp={false}
        onRequestClose={closeFolderDeleteModal}
        className="Modal__Content dark"
        parentSelector={() => document.querySelector('#root') as HTMLElement}
      >
        <div className="studio-project-modal">
          <p className="studio-project-modal--label">delete this folder</p>
          <p className="studio-project-modal--info">
            Are you sure you want to delete this folder? This action cannot be
            undone. Deleting the folder will permanently remove all its
            contents, including files contained within.
          </p>
          <div className="studio-project-modal--btns-container">
            <button
              className="studio-project-modal--btns-container--btn cancel"
              type="button"
              onClick={closeFolderDeleteModal}
            >
              cancel
            </button>
            <button
              className="studio-project-modal--btns-container--btn submit"
              type="button"
              onClick={onDeleteEmptyFolder}
            >
              delete forever
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ProjectsFolder;
